import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Rating, IconButton, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import { FeedbackCollectionProps, FeedbackData } from 'src/types/chat';

const useStyles = makeStyles((theme) => ({
    backdrop: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', // semi-transparent dark
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1300, // higher than other elements
        backdropFilter: 'blur(0px)', // optional (no blur)
    },
    container: {
        padding: theme.spacing(3),
        maxWidth: 400,
        width: '90%',
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[8],
        position: 'relative',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    title: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    ratingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    ratingLabel: {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    rating: {
        fontSize: '2rem',
    },
    commentsField: {
        marginBottom: theme.spacing(3),
    },
    buttonContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    cancelButton: {
        color: theme.palette.text.secondary,
    },
}));

const FeedbackCollection: React.FC<FeedbackCollectionProps> = ({
    orgId,
    projectId,
    customerId,
    caseKey,
    supportUserId,
    chatSessionId,
    onSubmit,
    onClose,
}) => {
    const classes = useStyles();
    const [rating, setRating] = useState<number>(0);
    const [comments, setComments] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = async () => {
        if (rating === 0) {
            return; // Don't submit if no rating is provided
        }

        setIsSubmitting(true);

        const feedbackData: FeedbackData = {
            orgId,
            projectId,
            customerId,
            caseKey,
            supportUserId,
            chatSessionId,
            rating,
            comments,
        };
        try {
            // Call the onSubmit callback if provided
            if (onSubmit) {
                await onSubmit(feedbackData);
            }

            // Close the feedback component after successful submission
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setRating(0);
        setComments('');
        if (onClose) {
            onClose();
        }
    };

    return (
        <Box className={classes.backdrop} onClick={(e) => e.stopPropagation()}>
            <Box className={classes.container} onClick={(e) => e.stopPropagation()}>
                <Box className={classes.header}>
                    <Typography variant="h6" className={classes.title}>
                        Rate Your Experience
                    </Typography>
                    <IconButton size="small" onClick={handleCancel}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider />

                <Box className={classes.ratingContainer}>
                    <Typography variant="body2" className={classes.ratingLabel}>
                        How would you rate your support experience?
                    </Typography>
                    <Rating
                        name="feedback-rating"
                        value={rating}
                        onChange={(event, newValue) => {
                            setRating(newValue || 0);
                        }}
                        size="large"
                        className={classes.rating}
                    />
                </Box>

                <TextField
                    label="Additional Comments (Optional)"
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Tell us about your experience..."
                    className={classes.commentsField}
                    inputProps={{ maxLength: 256 }}
                />

                <Box className={classes.buttonContainer}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        className={classes.cancelButton}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        className={classes.submitButton}
                        disabled={rating === 0 || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default FeedbackCollection;
